import React, {useEffect, useRef, useState} from 'react'
import {useAuth} from '../../../modules/auth'
import { showSuccessToast } from 'app/components/ShowToast'
import AssetDataService from '../services/asset.services'
import CustomDrawer from 'app/components/CustomDrawer'

export interface addAsset {
  msg: string
  message: string
  checked?: boolean | undefined
}
interface AddAssetsProps {
  isOpen: boolean
  onClose: () => void
}

const predefinedOSVersions = [
  // Windows
  'Windows 10',
  'Windows 11',
  'Windows 12',
  'Windows Server 2022',
  'Windows Server 2025',

  // macOS
  'macOS Sierra',
  'macOS High Sierra',
  'macOS Mojave',
  'macOS Catalina',
  'macOS Big Sur',
  'macOS Ventura',
  'macOS Sonoma',
  'macOS Sequoia',
  'macOS 16',

  // Ubuntu
  'Ubuntu 18.04',
  'Ubuntu 20.04', 
  'Ubuntu 22.04', 
  'Ubuntu 24.04',
  'Ubuntu 24.10', 
  'Ubuntu 25.04',

  // Linux Distros
  'Fedora 37',
  'Fedora 38',
  'Fedora 39',
  'Debian 10',
  'Debian 11',
  'Debian 12',
  'Debian 13',

  // Others
  'Chrome OS',
  'ChromeOS 128',
  'Android 12',
  'Android 13',
  'Android 14',
  'Android 15',
  'iOS 15',
  'iOS 16',
  'iOS 17',
  'iOS 18', 
  'iOS 19',
]

const AddAsset: React.FC<AddAssetsProps> = ({isOpen, onClose}) => {
  const [owner, setOwner] = useState('')
  const [name, setName] = useState('')
  const [serial_number, setSerialNumber] = useState('')
  const [model, setModel] = useState('')
  const [os_version, setOsVersion] = useState('')
  const [last_checkedin, setlast_checkedin] = useState('')
  const [hd_encryption, setHdEncryption] = useState('No')
  const [anti_virus, setAntiVirus] = useState('No')
  const [password_policy, setPasswordPolicy] = useState('No')
  const [screen_policy, setScreenPolicy] = useState('No')
  const [firewall, setFirewall] = useState('No')
  const [audit_scope, setAuditScope] = useState('No')
  const [showAlert, setShowAlert] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const {currentUser} = useAuth()
  const [owners, setOwners] = useState<any[]>([])
  const drawerTopRef = useRef(null)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [filteredOSVersions, setFilteredOSVersions] = useState<any[]>([])
  const focusRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (focusRef.current) {
          focusRef.current.focus()
        }
      }, 300)
    }
  }, [isOpen])

  if (showAlert && drawerTopRef.current) {
    ;(drawerTopRef.current as HTMLElement).scrollIntoView({behavior: 'smooth'})
  }

  useEffect(() => {
    AssetDataService.getEmployees(currentUser, setOwners)
  }, [currentUser])

  const handleInputChange = (e) => {
    const input = e.target.value
    setOsVersion(input)

    if (input.length > 0) {
      const filtered = predefinedOSVersions.filter((version) =>
        version.toLowerCase().includes(input.toLowerCase())
      )
      setFilteredOSVersions(filtered)
      setShowSuggestions(true)
    } else {
      setShowSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion) => {
    setOsVersion(suggestion)
    setShowSuggestions(false)
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!serial_number.trim()) {
      errors.serialNumber = 'Asset serial number is required'
    }

    if (!owner.trim()) {
      errors.owner = 'Asset owner is required'
    }

    if (!name.trim()) {
      errors.name = 'Asset name is required'
    }
    if (!model.trim()) {
      errors.model = 'Asset model is required'
    }

    if (!os_version.trim()) {
      errors.os_version = 'OS version is required'
    }
    if (!last_checkedin.trim()) {
      errors.last_checkedin = 'Last checkedin date is required'
    }

    // if (!hdEncryption.trim()) {
    //   errors.description = 'Control description is required'
    // }

    // if (!antiVirus.trim()) {
    //   errors.description = 'Control description is required'
    // }
    // if (!passwordPolicy.trim()) {
    //   errors.control = 'Control name is required'
    // }

    // if (!screenPolicy.trim()) {
    //   errors.description = 'Control description is required'
    // }

    // if (!firewall.trim()) {
    //   errors.control = 'Control name is required'
    // }

    // if (!auditScope.trim()) {
    //   errors.description = 'Control description is required'
    // }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsLoading(true)

    const newAsset = {
      serial_number,
      owner,
      name,
      model,
      os_version,
      last_checkedin,
      hd_encryption,
      anti_virus,
      password_policy,
      screen_policy,
      firewall,
      audit_scope,
    }

    if (!validateForm()) {
      setIsLoading(false)
      return
    }

    try {
      await AssetDataService.addAsset(newAsset, currentUser)
      setShowAlert(true)
      showSuccessToast({message:'New Asset added successfully'})

      window.scrollTo(0, 0)
    } catch (error) {
      console.error('Error adding asset:', error)
    } finally {
      setTimeout(() => {
        setShowAlert(false)
        onClose()
      }, 1000)
      setIsLoading(false)
      setName('')
      setOwner('')
      setModel('')
      setOsVersion('')
      setlast_checkedin('')
      setAntiVirus('')
      setHdEncryption('')
      setPasswordPolicy('')
      setScreenPolicy('')
      setFirewall('')
      setAuditScope('')
      setSerialNumber('')
    }
  }

  const toggleState = (currentState, setStateFunction) => {
    setStateFunction(currentState === 'Yes' ? 'No' : 'Yes')
  }

  const onCancel = () => {
    setFormErrors({})
    setName('')
    setOwner('')
    setModel('')
    setOsVersion('')
    setlast_checkedin('')
    setAntiVirus('')
    setHdEncryption('')
    setPasswordPolicy('')
    setScreenPolicy('')
    setFirewall('')
    setAuditScope('')
    setSerialNumber('')
    onClose()
  }

  return (
    <>
      <CustomDrawer
        isDrawerOpen={isOpen}
        onCancel={onCancel}
        title='Add Asset'
        loading={isLoading}
        submitHandler={handleSubmit}
        submitBtnText='Add Asset'
        submitBtnLoadingText='Add Asset...'
      >
        <form id='kt_risk_form' className='form' onSubmit={handleSubmit}>
          <div className='modal-content'>
            <div className='modal-body'>
              <div className=''>
                <div className='form-group'>
                  <label className='text-dark fw-bold text-primary my-2 fs-6 required'>
                    Serial Number
                  </label>

                  <input
                    type='text'
                    className={`form-control ${formErrors.serialNumber ? 'is-invalid' : ''}`}
                    placeholder='Serial number'
                    name='serialNumber'
                    value={serial_number}
                    onChange={(e) => {
                      setSerialNumber(e.target.value)
                      setFormErrors((prev) => ({...prev, serialNumber: ''}))
                    }}
                    ref={focusRef}
                  />
                  {formErrors.serialNumber && (
                    <span className='text-danger'>{formErrors.serialNumber}</span>
                  )}
                </div>

                <div className='form-group'>
                  <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                    Name of Asset
                  </label>

                  <input
                    type='text'
                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                    placeholder='Name'
                    name='name'
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value)
                      setFormErrors((prev) => ({...prev, name: ''}))
                    }}
                  />
                  {formErrors.name && <span className='text-danger'>{formErrors.name}</span>}
                </div>

                <div className='form-group'>
                  <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                    Model
                  </label>
                  <input
                    type='text'
                    className={`form-control ${formErrors.model ? 'is-invalid' : ''}`}
                    placeholder='Model'
                    value={model}
                    onChange={(e) => {
                      setModel(e.target.value)
                      setFormErrors((prev) => ({...prev, model: ''}))
                    }}
                  />
                  {formErrors.model && <span className='text-danger'>{formErrors.model}</span>}
                </div>

                <div className='form-group'>
                  <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                    OS Version
                  </label>
                  <input
                    type='text'
                    className={`form-control ${formErrors.os_version ? 'is-invalid' : ''}`}
                    placeholder='OS Version'
                    value={os_version}
                    // onChange={(e) => {
                    //   setOsVersion(e.target.value)
                    //   setFormErrors((prev) => ({...prev, os_version: ''}))
                    // }}
                    onChange={handleInputChange}
                    onBlur={() => setShowSuggestions(false)}
                    onFocus={() => os_version && setShowSuggestions(true)}
                  />
                  {showSuggestions && (
                    <ul
                      className='suggestions-list list-unstyled p-0 m-0 border rounded shadow'
                      style={{
                        position: 'absolute',
                        width: '100%',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        zIndex: 1000,
                      }}
                    >
                      {filteredOSVersions.map((suggestion, index) => (
                        <li
                          key={index}
                          onMouseDown={() => handleSuggestionClick(suggestion)}
                          className='suggestion-item px-3 fs-6 py-2 text-hover-primary'
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = '#f1f1f1')
                          }
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                        >
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  )}

                  {formErrors.os_version && (
                    <span className='text-danger'>{formErrors.os_version}</span>
                  )}
                </div>
                <div className='form-group'>
                  <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                    Owner Name
                  </label>

                  <select
                    name='owner'
                    className={`form-select ${formErrors.owner ? 'is-invalid' : ''}`}
                    data-placeholder='Select Owner'
                    value={owner}
                    onChange={(e) => {
                      setOwner(e.target.value)
                      setFormErrors((prev) => ({...prev, owner: ''}))
                    }}
                  >
                    <option value=''>Select Owner</option>
                    {owners
                      .filter((user) => user.role !== 'Auditor')
                      .map((owner) => (
                        <option key={owner.id} value={owner.full_name}>
                          {owner.full_name}
                        </option>
                      ))}
                  </select>
                  {formErrors.owner && <span className='text-danger'>{formErrors.owner}</span>}
                </div>
                <div className='form-group'>
                  <label className='text-dark fw-bold text-primary mt-6 my-2 fs-6 required'>
                    Last Checked-In
                  </label>

                  <input
                    type='date'
                    className={`form-control ${formErrors.last_checkedin ? 'is-invalid' : ''}`}
                    placeholder='Last checked-in'
                    value={last_checkedin}
                    onChange={(e) => {
                      setlast_checkedin(e.target.value)
                      setFormErrors((prev) => ({...prev, last_checkedin: ''}))
                    }}
                  />

                  {formErrors.last_checkedin && (
                    <span className='text-danger'>{formErrors.last_checkedin}</span>
                  )}
                </div>

                <div className='form-group w-100 mt-6'>
                  {/* Begin::Heading */}
                  <div className='mb-6'>
                    {/* Begin::Title */}
                    <h2 className='mb-3 fs-6'>Device Details</h2>
                    {/* End::Title */}

                    {/* Begin::Description */}
                    <div className='fw-semibold fs-6' style={{color: '#5A5A5A'}}>
                      Select all options that apply
                    </div>
                    {/* End::Description */}
                  </div>
                  {/* End::Heading */}

                  {/* Begin::Input group */}
                  <div
                    className='fv-row mb-15 fv-plugins-icon-container'
                    data-kt-buttons='true'
                    data-kt-initialized='1'
                  >
                    {/* Begin::Option */}
                    {/* <label
                      className={`form-check form-check-custom btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start p-6 mb-6 ${
                        hd_encryption === 'Yes' ? 'active' : ''
                      }`}
                    > */}
                    <label
                      className={`form-check form-check-custom btn btn-outline btn-outline-dashed d-flex text-start p-6 mb-6 ${
                        hd_encryption === 'Yes' ? 'active' : ''
                      }`}
                      style={{
                        backgroundColor: hd_encryption === 'Yes' ? '#E8F8F4' : 'transparent',
                        transition: 'background-color 0.3s',
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor = '#E8F8F4')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor =
                          hd_encryption === 'Yes' ? '#E8F8F4' : 'transparent')
                      }
                    >
                      {/* Begin::Checkbox */}
                      <input
                        type='checkbox'
                        checked={hd_encryption === 'Yes'}
                        onChange={() => toggleState(hd_encryption, setHdEncryption)}
                        className='form-check-input me-2 checkbox'
                      />
                      {/* End::Checkbox */}

                      {/* Begin::Label */}
                      <span className='d-flex align-items-center'>
                        {/* Begin::Info */}
                        <span className='ms-4'>
                          <span className='fs-6 fw-bold text-gray-900 mb-2 d-block'>
                            HD Encryption
                          </span>
                          <span className='fw-semibold fs-6 text-gray-700'>
                            The device's hard drive is fully encrypted
                          </span>
                        </span>
                        {/* End::Info */}
                      </span>
                      {/* End::Label */}
                    </label>
                    {/* End::Option */}

                    {/* Begin::Option */}
                    <label
                      className={`form-check form-check-custom mb-6 btn btn-outline btn-outline-dashed d-flex text-start p-6 ${
                        anti_virus === 'Yes' ? 'active' : ''
                      }`}
                      style={{
                        backgroundColor: anti_virus === 'Yes' ? '#E8F8F4' : 'transparent',
                        transition: 'background-color 0.3s',
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor = '#E8F8F4')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor =
                          anti_virus === 'Yes' ? '#E8F8F4' : 'transparent')
                      }
                    >
                      {/* Begin::Checkbox */}
                      <input
                        type='checkbox'
                        checked={anti_virus === 'Yes'}
                        onChange={() => toggleState(anti_virus, setAntiVirus)}
                        className='form-check-input me-2'
                      />
                      {/* End::Checkbox */}

                      {/* Begin::Label */}
                      <span className='d-flex align-items-center'>
                        {/* Begin::Info */}
                        <span className='ms-4'>
                          <span className='fs-6 fw-bold text-gray-900 mb-2 d-block'>
                            Antivirus
                          </span>
                          <span className='fw-semibold fs-6 text-gray-700'>
                            The device has up to date antivirus software installed and running
                          </span>
                        </span>
                        {/* End::Info */}
                      </span>
                      {/* End::Label */}
                    </label>
                    {/* End::Option */}

                    {/* Begin::Option */}
                    <label
                      className={`form-check form-check-custom mb-6 btn btn-outline btn-outline-dashed  d-flex text-start p-6 ${
                        password_policy === 'Yes' ? 'active' : ''
                      }`}
                      style={{
                        backgroundColor: password_policy === 'Yes' ? '#E8F8F4' : 'transparent',
                        transition: 'background-color 0.3s',
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor = '#E8F8F4')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor =
                          password_policy === 'Yes' ? '#E8F8F4' : 'transparent')
                      }
                    >
                      {/* Begin::Checkbox */}
                      <input
                        type='checkbox'
                        checked={password_policy === 'Yes'}
                        onChange={() => toggleState(password_policy, setPasswordPolicy)}
                        className='form-check-input me-2'
                      />
                      {/* End::Checkbox */}

                      {/* Begin::Label */}
                      <span className='d-flex align-items-center'>
                        {/* Begin::Info */}
                        <span className='ms-4'>
                          <span className='fs-6 fw-bold text-gray-900 mb-2 d-block'>
                            Password Policy
                          </span>
                          <span className='fw-semibold fs-6 text-gray-700'>
                            The device's password meets company requirements (e.g. length,
                            complexity)
                          </span>
                        </span>
                        {/* End::Info */}
                      </span>
                      {/* End::Label */}
                    </label>
                    {/* End::Option */}

                    {/* Begin::Option */}
                    <label
                      className={`form-check form-check-custom mb-6 btn btn-outline btn-outline-dashed d-flex text-start p-6 ${
                        screen_policy === 'Yes' ? 'active' : ''
                      }`}
                      style={{
                        backgroundColor: screen_policy === 'Yes' ? '#E8F8F4' : 'transparent',
                        transition: 'background-color 0.3s',
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor = '#E8F8F4')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor =
                          screen_policy === 'Yes' ? '#E8F8F4' : 'transparent')
                      }
                    >
                      {/* Begin::Checkbox */}
                      <input
                        type='checkbox'
                        checked={screen_policy === 'Yes'}
                        onChange={() => toggleState(screen_policy, setScreenPolicy)}
                        className='form-check-input me-2'
                      />
                      {/* End::Checkbox */}

                      {/* Begin::Label */}
                      <span className='d-flex align-items-center'>
                        {/* Begin::Info */}
                        <span className='ms-4'>
                          <span className='fs-6 fw-bold text-gray-900 mb-2 d-block'>
                            Screen Policy
                          </span>
                          <span className='fw-semibold fs-6 text-gray-700'>
                            The device automatically locks after a short period of inactivity and
                            requires a password/biometric to unlock
                          </span>
                        </span>
                        {/* End::Info */}
                      </span>
                      {/* End::Label */}
                    </label>
                    {/* End::Option */}

                    {/* Begin::Option */}
                    <label
                      className={`form-check form-check-custom mb-6 btn btn-outline btn-outline-dashed d-flex text-start p-6 ${
                        firewall === 'Yes' ? 'active' : ''
                      }`}
                      style={{
                        backgroundColor: firewall === 'Yes' ? '#E8F8F4' : 'transparent',
                        transition: 'background-color 0.3s',
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor = '#E8F8F4')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor =
                          firewall === 'Yes' ? '#E8F8F4' : 'transparent')
                      }
                    >
                      {/* Begin::Checkbox */}
                      <input
                        type='checkbox'
                        checked={firewall === 'Yes'}
                        onChange={() => toggleState(firewall, setFirewall)}
                        className='form-check-input me-2'
                      />
                      {/* End::Checkbox */}

                      {/* Begin::Label */}
                      <span className='d-flex align-items-center'>
                        {/* Begin::Info */}
                        <span className='ms-4'>
                          <span className='fs-6 fw-bold text-gray-900 mb-2 d-block'>
                            Firewall
                          </span>
                          <span className='fw-semibold fs-6 text-gray-700'>
                            The device has firewall enabled to protect against unauthorized
                            network access
                          </span>
                        </span>
                        {/* End::Info */}
                      </span>
                      {/* End::Label */}
                    </label>
                    {/* End::Option */}

                    {/* Begin::Option */}
                    <label
                      className={`form-check form-check-custom mb-6 btn btn-outline btn-outline-dashed d-flex text-start p-6 ${
                        audit_scope === 'Yes' ? 'active' : ''
                      }`}
                      style={{
                        backgroundColor: audit_scope === 'Yes' ? '#E8F8F4' : 'transparent',
                        transition: 'background-color 0.3s',
                      }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor = '#E8F8F4')
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.backgroundColor =
                          audit_scope === 'Yes' ? '#E8F8F4' : 'transparent')
                      }
                    >
                      {/* Begin::Checkbox */}
                      <input
                        type='checkbox'
                        checked={audit_scope === 'Yes'}
                        onChange={() => toggleState(audit_scope, setAuditScope)}
                        className='form-check-input me-2'
                      />
                      {/* End::Checkbox */}

                      {/* Begin::Label */}
                      <span className='d-flex align-items-center'>
                        {/* Begin::Info */}
                        <span className='ms-4'>
                          <span className='fs-6 fw-bold text-gray-900 mb-2 d-block'>
                            Audit Scope
                          </span>
                          <span className='fw-semibold fs-6 text-gray-700'>
                            The device is within your audit scope.
                          </span>
                        </span>
                        {/* End::Info */}
                      </span>
                      {/* End::Label */}
                    </label>
                    {/* End::Option */}

                    <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'></div>
                  </div>
                  {/* End::Input group */}
                </div>
              </div>
            </div>
          </div>
        </form>
      </CustomDrawer>
    </>
  )
}

export default AddAsset
